// Broiler batch public-mirror helper.
//
// Single source of truth for the public-anon broiler weigh-in form's batch
// list + per-batch schooner labels, mirrored from the admin app_store ppp-v4
// rows into webform_config so the public form never needs anon SELECT on
// app_store. Shared by:
//   - src/app/maintenance/webformMirrorPayloads.ts (canonical mirror payloads)
//   - src/webforms/WeighInsWebform.tsx (column labels for the broiler grid)
//
// Filter contract: status === 'active'. Public broiler weigh-ins are for
// active batches only (Ronnie 2026-04-30 follow-up after a planned batch
// surfaced in the public dropdown post-rollout). Empty-schooner handling
// remains — an active batch with no schooners still surfaces in the
// dropdown and blocks Start Session via deriveBroilerColumnLabels = [].

// One admin ppp-v4 batch row, as the mirror writer sees it. `schooner` is the
// raw '&'-joined string on the row; `schooners` (below) is the split form that
// actually ships to webform_config. Every field is optional because the mirror
// runs over whatever the app_store row happens to carry.
export type BroilerBatchRow = {
  name?: string | null;
  status?: string | null;
  schooner?: string | null;
  brooder?: number | string | null;
  brooderOut?: string | null;
};

// One entry in the public mirror's `meta` array — the shape the public webform
// reads back out of webform_config.
export type BroilerBatchMetaEntry = {
  name: string | null | undefined;
  schooners: string[];
  brooder: number | string | null;
  brooderOut: string | null;
};

export type BroilerPublicMirror = {
  groups: Array<string | null | undefined>;
  meta: BroilerBatchMetaEntry[];
};

export function splitSchooners(raw: unknown): string[] {
  return String(raw || '')
    .split('&')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function buildBroilerPublicMirror(batchRows: BroilerBatchRow[] | null | undefined): BroilerPublicMirror {
  const active = (batchRows || []).filter((b) => b && b.status === 'active');
  return {
    groups: active.map((b) => b.name),
    meta: active.map((b) => ({
      name: b.name,
      schooners: splitSchooners(b.schooner),
      brooder: b.brooder || null,
      brooderOut: b.brooderOut || null,
    })),
  };
}

export function deriveBroilerColumnLabels(
  meta: BroilerBatchMetaEntry[] | null | undefined,
  batchId: string | null | undefined,
): string[] {
  const list = Array.isArray(meta) ? meta : [];
  const rec = list.find((b) => b && b.name === batchId);
  if (!rec || !Array.isArray(rec.schooners)) return [];
  return rec.schooners.filter(Boolean);
}

// Both maps are looked up with a runtime-computed key and fall back to a
// generated label on a miss, so an index signature (not the inferred literal
// keys) is the accurate type — the misses are expected, not errors.
const SCHOONER_LABELS: Record<string, string> = {
  1: 'Schooner 1',
  '2&3': 'Schooner 2 & 3',
  '4&5': 'Schooner 4 & 5',
  '6&6A': 'Schooner 6 & 6A',
  '7&7A': 'Schooner 7 & 7A',
};

const BROODER_LABELS: Record<string, string> = {1: 'Brooder 1', 2: 'Brooder 2', 3: 'Brooder 3'};

// Where the batch is housed right now — "Schooner 2 & 3", "Brooder 1" — or ''
// when the meta says nothing. Split out of formatBroilerBatchLabel so a caller
// that shows the name and its housing in SEPARATE places (the Add Report group
// pills put the name on the pill and the housing underneath) does not have to
// pick the parenthetical back out of a formatted string.
export function broilerBatchHousingLabel(
  name: string | null | undefined,
  meta: BroilerBatchMetaEntry[] | null | undefined,
): string {
  if (!name) return '';
  const list = Array.isArray(meta) ? meta : [];
  const rec = list.find((b) => b && b.name === name);
  if (!rec) return '';
  const inSchooner = rec.brooderOut && new Date(rec.brooderOut + 'T12:00:00') <= new Date();
  if (inSchooner && rec.schooners && rec.schooners.length > 0) {
    const raw = rec.schooners.join('&');
    return SCHOONER_LABELS[raw] || 'Schooner ' + rec.schooners.join(' & ');
  }
  if (rec.brooder) {
    return BROODER_LABELS[rec.brooder] || 'Brooder ' + rec.brooder;
  }
  return '';
}

export function formatBroilerBatchLabel(
  name: string | null | undefined,
  meta: BroilerBatchMetaEntry[] | null | undefined,
): string {
  if (!name) return '';
  const housing = broilerBatchHousingLabel(name, meta);
  return housing ? name + ' (' + housing + ')' : name;
}
